import { useCallback, useState } from 'react';
import { Flex, useToast, Text } from '@chakra-ui/react';
import { GetParamsObject } from '@/model/query-params';
import { useFiltersForm } from '@/utils/useFiltersForm';
import { SectionBox } from '@/components/SectionBox';
import CenteredSpinner from '@/components/CenterSpinner/CenteredSpinner';
import { PartnersFiltersBox } from './components/PartnersFiltersBox';
import { usePartners, useGetPartnerByUUID, useDeletePartnerByIdMutation } from '@/services/queries/partner';
import { type PartnersFilters } from '../../types';
import { PartnersTable } from './components/PartnersTable/PartnersTable';
import { PartnerEditModal } from './components/PartnerEditModal';
import { PartnerCreateModal } from './components/PartnerCreateModal';
import { DateFilterInputValue } from '@/components/DateRangeFilterFields/types';
import { Partner } from '@/model';
import { useTranslation } from 'react-i18next';
import { ConfirmationModal } from '@/components/ConfirmationModal';

type PartnerSectionProps = {
  isShowCreateModal: boolean;
  setIsShowCreateModal: (value: boolean) => void;
};

export const PartnersSection = ({ isShowCreateModal, setIsShowCreateModal }: PartnerSectionProps) => {
  const { t } = useTranslation();

  const [selectedItem, setSelectedItem] = useState<Partner | undefined>(undefined);

  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
  const [isShowEditModal, setIsShowEditModal] = useState<boolean>(false);

  const [partnersSorting, setPartnersSorting] = useState<{ field: string; desc: boolean }>({ field: 'created_at', desc: true });
  const [partnersPagination, setPartnersPagination] = useState<{ pageNumber: number; pageSize: number }>({
    pageNumber: 1,
    pageSize: 10,
  });

  const partnersFiltersForm = useFiltersForm<PartnersFilters>({
    defaultValues: {
      name: '',
      start_date: '',
      end_date: '',
      dateValue: DateFilterInputValue.ALL,
    },
  });

  const partnersFilterData = partnersFiltersForm.watch();

  const params = GetParamsObject(
    partnersFilterData,
    partnersPagination.pageNumber,
    partnersPagination.pageSize,
    partnersSorting.desc ? 'desc' : 'asc',
    partnersSorting.field,
  );

  const { data: partnersData, isLoading: isLoadingPartnersData, forceRefetch } = usePartners(params);

  const getParterByUUID = useGetPartnerByUUID();
  const deleteMutation = useDeletePartnerByIdMutation();

  const toast = useToast();

  const partners = partnersData?.data || [];

  const handlePartnerEdit = (partnerUUID: string) => {
    getParterByUUID.mutate(partnerUUID, {
      onSuccess: res => {
        setSelectedItem(res);
        setIsShowEditModal(true);
      },
      onError: (resp: any) => {
        toast({
          status: 'error',
          title:
            (resp && resp.message) ||
            t('pages.partners.partners_section.partners_section.partners_section_error_partner_not_found'),
        });
      },
    });
  };

  const onHandleDelete = (item: Partner) => {
    setSelectedItem(item);
    setIsConfirmModalOpen(true);
  };

  const onHandleDeleteConfirm = () => {
    if (!selectedItem?.uuid) return;

    setIsConfirmModalOpen(false);

    deleteMutation.mutate(selectedItem.uuid, {
      onSuccess: () => {
        toast({
          title: t('pages.partners.partners_section.partners_section_delete_success'),
          status: 'success',
        });
        forceRefetch();
        setSelectedItem(undefined);
      },
      onError: (resp: any) => {
        setSelectedItem(undefined);
        toast({
          status: 'error',
          title: (resp && resp.message) || t('pages.partners.partners_section.partners_section_delete_error'),
        });
      },
    });
  };

  const onHandleEditDone = useCallback(() => {
    setIsShowEditModal(false);
    setSelectedItem(undefined);
    forceRefetch();
  }, [forceRefetch]);

  const onHandleCreateDone = useCallback(() => {
    setIsShowCreateModal(false);
    forceRefetch();
  }, [forceRefetch, setIsShowCreateModal]);

  return (
    <>
      <SectionBox bodyProps={{ gap: 'small' }}>
        <PartnersFiltersBox filtersForm={partnersFiltersForm} />
      </SectionBox>

      <Flex direction='column' gap='deka' mt={8}>
        <PartnersTable
          data={partners}
          isLoading={isLoadingPartnersData}
          sorting={partnersSorting}
          pageNumber={partnersPagination.pageNumber}
          pageSize={partnersPagination.pageSize}
          totalPages={partnersData?.meta.pages || 0}
          totalItems={partnersData?.meta.total || 0}
          onSortingChange={(field, desc) => setPartnersSorting({ field, desc })}
          onPaginationChange={(pageNumber, pageSize) => setPartnersPagination({ pageNumber, pageSize })}
          onPartnerEditClick={item => handlePartnerEdit(item.uuid)}
          onPartnerDeleteClick={item => onHandleDelete(item)}
        />
      </Flex>

      <ConfirmationModal
        isOpen={isConfirmModalOpen}
        onClose={() => {
          setIsConfirmModalOpen(false);
          setSelectedItem(undefined);
        }}
        onConfirm={onHandleDeleteConfirm}
        isLoading={false}
        title={t('pages.partners.partners_section.partners_section_delete_confirm_title')}
        description={
          <Text>
            {t('pages.partners.partners_section.partners_section_delete_confirm_description_start')}
            <Text as='span' fontWeight='bold'>
              "{selectedItem?.name}"
            </Text>
            {t('pages.partners.partners_section.partners_section_delete_confirm_description_end')}
          </Text>
        }
      />

      {selectedItem && (
        <PartnerEditModal
          isOpen={!!isShowEditModal}
          onClose={() => {
            setIsShowEditModal(false);
            setSelectedItem(undefined);
          }}
          partner={selectedItem}
          onDone={onHandleEditDone}
        />
      )}

      <PartnerCreateModal isOpen={!!isShowCreateModal} onClose={() => setIsShowCreateModal(false)} onDone={onHandleCreateDone} />

      <CenteredSpinner isLoading={getParterByUUID.isLoading} />
    </>
  );
};
